/**
 * Language Selector Component
 * Allows users to select their preferred language
 * Uses TranslationContext for DB-backed translations with caching
 */

import { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface LanguageSelectorProps {
  value?: string;
  onChange?: (languageCode: string) => void;
  showLabel?: boolean;
}

export function LanguageSelector({ value, onChange, showLabel = true }: LanguageSelectorProps) {
  // Use the unified translation context (DB + static fallback)
  const { t, language, setLanguage, availableLanguages, isLoading } = useAppTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(value || language);

  useEffect(() => {
    if (value) {
      setSelectedLanguage(value);
    }
  }, [value]);

  // Sync with context language
  useEffect(() => {
    if (language !== selectedLanguage && !value) {
      setSelectedLanguage(language);
    }
  }, [language]);

  const handleLanguageChange = async (languageCode: string) => {
    setSelectedLanguage(languageCode);

    // Use the context's setLanguage which handles:
    // 1. Changing i18n language
    // 2. Fetching DB translations for new language
    // 3. Caching translations in localStorage
    await setLanguage(languageCode);

    // Notify parent component
    if (onChange) {
      onChange(languageCode);
    }
  };

  if (isLoading && availableLanguages.length === 0) {
    return (
      <div className="space-y-2">
        {showLabel && <Label>{t('settings.language', 'Language')}</Label>}
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {showLabel && <Label htmlFor="language-select">{t('settings.language', 'Language')}</Label>}
      <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
        <SelectTrigger id="language-select" className="w-full">
          <SelectValue placeholder={t('settings.select_language', 'Select language')} />
        </SelectTrigger>
        <SelectContent>
          {availableLanguages.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <div className="flex items-center gap-2">
                <span>{lang.nativeName}</span>
                {lang.name !== lang.nativeName && (
                  <span className="text-muted-foreground">({lang.name})</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
